import {
    AccessRole,
    AccessAssignmentMutationInput,
    AssignmentCardMutationInput,
    AssignmentStatus,
    CreateOrganizationInvitationInput,
    CreateOrganizationAdminInvitationInput,
    DeleteStructureNodeInput,
    DeleteStructureNodeResult,
    EditMemberProfileInput,
    InvitationLifecycleInput,
    InvitationDecision,
    InvitationDecisionInput,
    InvitationDecisionResult,
    InvitationIntent,
    InvitationReview,
    InvitationReviewItem,
    MembershipStatus,
    MemberAccessMutationInput,
    MemberStatusMutationInput,
    OrganizationAccessContext,
    OrganizationContextMembership,
    OrganizationContextState,
    OrganizationMemberDirectory,
    OrganizationInvitation,
    OrganizationInvitationDirectory,
    OrganizationMembership,
    RoleAssignment,
    RemoveAccessAssignmentInput,
    RemoveAssignmentCardInput,
    RevokeInvitationInput,
    ScopeType,
    RenameStructureNodeInput,
    StructureNodeMutationInput,
    UpdatedMemberProfile,
    UnitClient,
    UnitClientInput,
    UnitStaffCandidate,
    UnitStaffMember,
    VisibleScope,
    WorkAssignment
} from "../pages/organization-admin/types";

export class OrganizationAccessApiError extends Error {
    public readonly status: number;

    public constructor(message: string, status: number) {
        super(message);
        this.name = "OrganizationAccessApiError";
        this.status = status;
    }
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const requiredString = (record: Record<string, unknown>, key: string): string => {
    const value = record[key];
    if (typeof value !== "string") {
        throw new OrganizationAccessApiError(`Invalid Organization API field: ${key}`, 500);
    }
    return value;
};

const optionalString = (record: Record<string, unknown>, key: string): string | undefined => {
    const value = record[key];
    if (value === undefined || value === null) {
        return undefined;
    }
    if (typeof value !== "string") {
        throw new OrganizationAccessApiError(`Invalid Organization API field: ${key}`, 500);
    }
    return value;
};

const nullableString = (record: Record<string, unknown>, key: string): string | null => {
    const value = record[key];
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value !== "string") {
        throw new OrganizationAccessApiError(`Invalid Organization API field: ${key}`, 500);
    }
    return value;
};

const requiredBoolean = (record: Record<string, unknown>, key: string): boolean => {
    const value = record[key];
    if (typeof value !== "boolean") {
        throw new OrganizationAccessApiError(`Invalid Organization API field: ${key}`, 500);
    }
    return value;
};

const requiredNumber = (record: Record<string, unknown>, key: string): number => {
    const value = record[key];
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new OrganizationAccessApiError(`Invalid Organization API field: ${key}`, 500);
    }
    return value;
};

const requiredArray = (record: Record<string, unknown>, key: string): unknown[] => {
    const value = record[key];
    if (!Array.isArray(value)) {
        throw new OrganizationAccessApiError(`Invalid Organization API field: ${key}`, 500);
    }
    return value;
};

const optionalStringArray = (record: Record<string, unknown>, key: string): string[] | undefined => {
    const value = record[key];
    if (value === undefined || value === null) {
        return undefined;
    }
    if (!Array.isArray(value) || value.some(item => typeof item !== "string")) {
        throw new OrganizationAccessApiError(`Invalid Organization API field: ${key}`, 500);
    }
    return value.filter((item): item is string => typeof item === "string");
};

const parseScopeType = (value: string): ScopeType => {
    if (value === "Organization" || value === "Business Area" || value === "Unit") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API scope type", 500);
};

const parseAccessRole = (value: string): AccessRole => {
    if (value === "Organization Admin" || value === "Business Area Admin" || value === "Unit Manager" || value === "Care Worker") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API access role", 500);
};

const parseMembershipStatus = (value: string): MembershipStatus => {
    if (value === "Active" || value === "Suspended" || value === "Revoked") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API membership status", 500);
};

const parseAssignmentStatus = (value: string): AssignmentStatus => {
    if (value === "Active" || value === "Ended") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API assignment status", 500);
};

const parseUnitStaffSource = (value: string): UnitStaffMember["source"] => {
    if (value === "RoleAssignment" || value === "InheritedRoleAssignment" || value === "UnitWorkspaceMembership" || value === "WorkAssignment") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API Unit Staff source", 500);
};

const parseContextState = (value: string): OrganizationContextState => {
    if (value === "selected" || value === "selection_required" || value === "no_access") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API context state", 500);
};

const parseRoleAssignment = (value: unknown): RoleAssignment => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Organization API role assignment", 500);
    }
    return {
        id: requiredString(value, "id"),
        scopeId: requiredString(value, "scopeId"),
        scopeName: requiredString(value, "scopeName"),
        scopeType: parseScopeType(requiredString(value, "scopeType")),
        coveredScopeIds: optionalStringArray(value, "coveredScopeIds"),
        role: parseAccessRole(requiredString(value, "role")),
        status: parseAssignmentStatus(requiredString(value, "status"))
    };
};

const parseWorkAssignment = (value: unknown): WorkAssignment => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Organization API work assignment", 500);
    }
    return {
        id: requiredString(value, "id"),
        scopeId: requiredString(value, "scopeId"),
        scopeName: requiredString(value, "scopeName"),
        scopeType: parseScopeType(requiredString(value, "scopeType")),
        jobTitle: requiredString(value, "jobTitle"),
        teamName: optionalString(value, "teamName"),
        teamId: optionalString(value, "teamId"),
        originVisible: requiredBoolean(value, "originVisible"),
        coveredScopeIds: optionalStringArray(value, "coveredScopeIds"),
        status: parseAssignmentStatus(requiredString(value, "status"))
    };
};

const parseInvitation = (value: unknown): OrganizationInvitation => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Organization API invitation", 500);
    }
    return {
        id: requiredString(value, "id"),
        version: requiredNumber(value, "version"),
        name: requiredString(value, "name"),
        firstName: requiredString(value, "firstName"),
        lastName: requiredString(value, "lastName"),
        email: requiredString(value, "email"),
        workPhone: nullableString(value, "workPhone"),
        status: parseInvitationStatus(requiredString(value, "status")),
        deliveryStatus: parseDeliveryStatus(requiredString(value, "deliveryStatus")),
        workAssignment: parseWorkAssignment(value.workAssignment),
        roleAssignment: parseRoleAssignment(value.roleAssignment),
        invitedBy: requiredString(value, "invitedBy"),
        canManage: requiredBoolean(value, "canManage"),
        accessReason: requiredString(value, "accessReason"),
        createdAt: requiredString(value, "createdAt"),
        updatedAt: requiredString(value, "updatedAt"),
        updatedBy: requiredString(value, "updatedBy"),
        expiresAt: requiredString(value, "expiresAt")
    };
};

const parseInvitationStatus = (value: string): OrganizationInvitation["status"] => {
    if (value === "Pending" || value === "Opened" || value === "Accepted" || value === "Declined" || value === "Expired" || value === "Revoked") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API invitation status", 500);
};

const parseDeliveryStatus = (value: string): OrganizationInvitation["deliveryStatus"] => {
    if (value === "Queued" || value === "Sent" || value === "Failed") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Organization API delivery status", 500);
};

const parseContextMembership = (value: unknown): OrganizationContextMembership => {
    if (!isRecord(value) || !isRecord(value.organization)) {
        throw new OrganizationAccessApiError("Invalid Organization API membership context", 500);
    }
    return {
        membershipId: optionalString(value, "membershipId"),
        organization: {
            id: requiredString(value.organization, "id"),
            name: requiredString(value.organization, "name")
        },
        explicitRoleAssignments: requiredArray(value, "explicitRoleAssignments").map(parseRoleAssignment)
    };
};

const parseContext = (value: unknown): OrganizationAccessContext => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Organization API context", 500);
    }
    return {
        state: parseContextState(requiredString(value, "state")),
        currentOrganizationId: nullableString(value, "currentOrganizationId"),
        memberships: requiredArray(value, "memberships").map(parseContextMembership)
    };
};

const parseVisibleScope = (value: unknown): VisibleScope => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Organization API visible scope", 500);
    }
    return {
        id: requiredString(value, "id"),
        name: requiredString(value, "name"),
        scopeType: parseScopeType(requiredString(value, "scopeType")),
        parentId: nullableString(value, "parentId"),
        address: nullableString(value, "address"),
        version: requiredNumber(value, "version")
    };
};

const parseMembership = (value: unknown): OrganizationMembership => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Organization API member", 500);
    }
    return {
        id: requiredString(value, "id"),
        version: requiredNumber(value, "version"),
        firstName: requiredString(value, "firstName"),
        lastName: requiredString(value, "lastName"),
        email: requiredString(value, "email"),
        workPhone: requiredString(value, "workPhone"),
        status: parseMembershipStatus(requiredString(value, "status")),
        workAssignments: requiredArray(value, "workAssignments").map(parseWorkAssignment),
        roleAssignments: requiredArray(value, "roleAssignments").map(parseRoleAssignment),
        isCurrentMembership: requiredBoolean(value, "isCurrentMembership"),
        readOnlyReason: optionalString(value, "readOnlyReason")
    };
};

const parseUpdatedMemberProfile = (value: unknown): UpdatedMemberProfile => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Organization API member profile", 500);
    }
    return {
        id: requiredString(value, "id"),
        version: requiredNumber(value, "version"),
        firstName: requiredString(value, "firstName"),
        lastName: requiredString(value, "lastName"),
        workPhone: requiredString(value, "workPhone"),
        status: parseMembershipStatus(requiredString(value, "status"))
    };
};

const parseUnitClient = (value: unknown): UnitClient => {
    if (!isRecord(value)) throw new OrganizationAccessApiError("Invalid Unit Client", 500);
    return {
        id: requiredString(value, "id"),
        organizationId: requiredString(value, "organizationId"),
        businessAreaId: requiredString(value, "businessAreaId"),
        businessAreaName: requiredString(value, "businessAreaName"),
        unitId: requiredString(value, "unitId"),
        unitName: requiredString(value, "unitName"),
        name: requiredString(value, "name"),
        phoneNumber: requiredString(value, "phoneNumber"),
        email: requiredString(value, "email"),
        preferencesNotes: requiredString(value, "preferencesNotes"),
        status: requiredString(value, "status"),
        createdAt: requiredString(value, "createdAt"),
        updatedAt: requiredString(value, "updatedAt"),
        version: requiredNumber(value, "version")
    };
};

const parseUnitStaffMember = (value: unknown): UnitStaffMember => {
    if (!isRecord(value)) throw new OrganizationAccessApiError("Invalid Unit Staff member", 500);
    return {
        id: requiredString(value, "id"),
        membershipVersion: requiredNumber(value, "membershipVersion"),
        workAssignmentId: optionalString(value, "workAssignmentId"),
        roleAssignmentId: optionalString(value, "roleAssignmentId"),
        unitWorkspaceMembershipId: optionalString(value, "unitWorkspaceMembershipId"),
        source: parseUnitStaffSource(requiredString(value, "source")),
        canRemoveFromUnitWorkspace: requiredBoolean(value, "canRemoveFromUnitWorkspace"),
        name: requiredString(value, "name"),
        jobTitle: requiredString(value, "jobTitle"),
        email: requiredString(value, "email"),
        status: parseMembershipStatus(requiredString(value, "status"))
    };
};

const parseUnitStaffCandidate = (value: unknown): UnitStaffCandidate => {
    if (!isRecord(value)) throw new OrganizationAccessApiError("Invalid Unit Staff candidate", 500);
    return {
        id: requiredString(value, "id"),
        name: requiredString(value, "name"),
        email: requiredString(value, "email"),
        defaultJobTitle: requiredString(value, "defaultJobTitle"),
        status: parseMembershipStatus(requiredString(value, "status")),
        alreadyInUnitWorkspace: requiredBoolean(value, "alreadyInUnitWorkspace"),
        workspaceSource: optionalString(value, "workspaceSource") as UnitStaffCandidate["workspaceSource"]
    };
};

const parseDirectory = (value: unknown): OrganizationMemberDirectory => {
    if (!isRecord(value) || !isRecord(value.organization)) {
        throw new OrganizationAccessApiError("Invalid Organization API member directory", 500);
    }
    return {
        organization: {
            id: requiredString(value.organization, "id"),
            name: requiredString(value.organization, "name")
        },
        actorMembershipId: requiredString(value, "actorMembershipId"),
        canInviteOrganizationAdmin: requiredBoolean(value, "canInviteOrganizationAdmin"),
        visibleScopes: requiredArray(value, "visibleScopes").map(parseVisibleScope),
        navigationScopes: requiredArray(value, "navigationScopes").map(parseVisibleScope),
        members: requiredArray(value, "members").map(parseMembership)
    };
};

const parseInvitationDirectory = (value: unknown): OrganizationInvitationDirectory => {
    if (!isRecord(value) || !isRecord(value.organization)) {
        throw new OrganizationAccessApiError("Invalid Organization API invitation directory", 500);
    }
    return {
        organization: {
            id: requiredString(value.organization, "id"),
            name: requiredString(value.organization, "name")
        },
        actorMembershipId: requiredString(value, "actorMembershipId"),
        invitations: requiredArray(value, "invitations").map(parseInvitation)
    };
};

const parseInvitationIntent = (value: string): InvitationIntent => {
    if (value === "accept" || value === "decline") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Invitation review intent", 500);
};

const parseInvitationDecision = (value: string): InvitationDecision => {
    if (value === "Accepted" || value === "Declined") {
        return value;
    }
    throw new OrganizationAccessApiError("Invalid Invitation decision", 500);
};

const parseReviewScope = (value: unknown): InvitationReviewItem["workScope"] => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Invitation review scope", 500);
    }
    return {
        id: requiredString(value, "id"),
        name: requiredString(value, "name"),
        scopeType: parseScopeType(requiredString(value, "scopeType"))
    };
};

const parseInvitationReviewItem = (value: unknown): InvitationReviewItem => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Invitation review item", 500);
    }
    return {
        id: requiredString(value, "id"),
        version: requiredNumber(value, "version"),
        workScope: parseReviewScope(value.workScope),
        jobTitle: requiredString(value, "jobTitle"),
        team: nullableString(value, "team"),
        role: parseAccessRole(requiredString(value, "role")),
        roleScope: parseReviewScope(value.roleScope),
        invitedBy: requiredString(value, "invitedBy"),
        accessReason: requiredString(value, "accessReason"),
        expiresAt: requiredString(value, "expiresAt")
    };
};

const parseInvitationReview = (value: unknown): InvitationReview => {
    if (!isRecord(value) || !isRecord(value.organization)) {
        throw new OrganizationAccessApiError("Invalid Invitation review", 500);
    }
    return {
        organization: {
            id: requiredString(value.organization, "id"),
            name: requiredString(value.organization, "name")
        },
        intent: parseInvitationIntent(requiredString(value, "intent")),
        membershipProfile: (() => {
            if (!isRecord(value.membershipProfile)) {
                throw new OrganizationAccessApiError("Invalid Invitation membership profile", 500);
            }
            return {
                firstName: requiredString(value.membershipProfile, "firstName"),
                lastName: requiredString(value.membershipProfile, "lastName"),
                workPhone: nullableString(value.membershipProfile, "workPhone")
            };
        })(),
        invitations: requiredArray(value, "invitations").map(parseInvitationReviewItem)
    };
};

const parseInvitationDecisionResult = (value: unknown): InvitationDecisionResult => {
    if (!isRecord(value)) {
        throw new OrganizationAccessApiError("Invalid Invitation decision result", 500);
    }
    return {
        decision: parseInvitationDecision(requiredString(value, "decision")),
        invitationCount: requiredNumber(value, "invitationCount"),
        membershipId: nullableString(value, "membershipId"),
        membershipCreated: requiredBoolean(value, "membershipCreated")
    };
};

const requestJson = async (url: string, init: RequestInit = {}): Promise<unknown> => {
    const response = await fetch(url, {
        ...init,
        credentials: "same-origin"
    });
    const responseBody = await response.text();
    let payload: unknown;
    try {
        payload = responseBody ? JSON.parse(responseBody) : null;
    } catch {
        const contentType = response.headers.get("Content-Type")?.toLocaleLowerCase() ?? "";
        const message = contentType.includes("text/html")
            ? "The Organization API returned a web page instead of data. The backend route may be unavailable or your session may have expired."
            : "The Organization API returned an invalid response.";
        throw new OrganizationAccessApiError(message, response.status || 502);
    }
    if (!response.ok) {
        const message = isRecord(payload) && typeof payload.error === "string" ? payload.error : "Organization request failed";
        throw new OrganizationAccessApiError(message, response.status);
    }
    return payload;
};

const requestNoContent = async (url: string, init: RequestInit = {}): Promise<void> => {
    const response = await fetch(url, {
        ...init,
        credentials: "same-origin"
    });
    if (!response.ok) {
        throw new OrganizationAccessApiError("Organization request failed", response.status);
    }
};

/**
 * Resolve the authenticated account's Organization context.
 * @throws {OrganizationAccessApiError} When the request fails or the response is invalid.
 */
export const getOrganizationContext = async (idToken?: string): Promise<OrganizationAccessContext> => {
    const payload = await requestJson("/api/organization-access/context", {
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : {}
    });
    return parseContext(payload);
};

/**
 * Persist an explicitly selected Organization for the authenticated account.
 * @throws {OrganizationAccessApiError} When selection is denied, the request fails, or the response is invalid.
 */
export const selectOrganization = async (organizationId: string, idToken?: string): Promise<OrganizationAccessContext> => {
    const payload = await requestJson("/api/organization-access/context", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({ organizationId })
    });
    return parseContext(payload);
};

/**
 * Read the Members visible to the current administrator inside one Organization.
 * @throws {OrganizationAccessApiError} When access is denied, the request fails, or the response is invalid.
 */
export const getOrganizationMembers = async (organizationId: string, idToken?: string): Promise<OrganizationMemberDirectory> => {
    const payload = await requestJson(`/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members`, {
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : {}
    });
    return parseDirectory(payload);
};

/** Read Invitations whose Work and Role scopes are both visible to the administrator. */
export const getOrganizationInvitations = async (organizationId: string, idToken?: string): Promise<OrganizationInvitationDirectory> => {
    const payload = await requestJson(`/api/organization-access/organizations/${encodeURIComponent(organizationId)}/invitations`, {
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : {}
    });
    return parseInvitationDirectory(payload);
};

/** Create one immutable Work and Role proposal for a recipient. */
export const createOrganizationInvitation = async (
    organizationId: string,
    input: CreateOrganizationInvitationInput,
    idToken?: string
): Promise<OrganizationInvitation> => {
    const payload = await requestJson(`/api/organization-access/organizations/${encodeURIComponent(organizationId)}/invitations`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify(input)
    });
    return parseInvitation(payload);
};

export const createOrganizationAdminInvitation = async (
    organizationId: string,
    input: CreateOrganizationAdminInvitationInput,
    idToken?: string
): Promise<OrganizationInvitation> => {
    const payload = await requestJson(`/api/organization-access/organizations/${encodeURIComponent(organizationId)}/organization-admin-invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
        body: JSON.stringify(input)
    });
    return parseInvitation(payload);
};

export const createStructureNode = async (organizationId: string, input: StructureNodeMutationInput, idToken?: string): Promise<VisibleScope> => {
    const payload = await requestJson(`/api/organization-access/organizations/${encodeURIComponent(organizationId)}/structure-nodes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
        body: JSON.stringify(input)
    });
    return parseVisibleScope(payload);
};

export const renameStructureNode = async (organizationId: string, nodeId: string, input: RenameStructureNodeInput, idToken?: string): Promise<VisibleScope> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/structure-nodes/${encodeURIComponent(nodeId)}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseVisibleScope(payload);
};

export const deleteStructureNode = async (
    organizationId: string,
    nodeId: string,
    input: DeleteStructureNodeInput,
    idToken?: string
): Promise<DeleteStructureNodeResult> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/structure-nodes/${encodeURIComponent(nodeId)}`,
        {
            method: "DELETE",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    if (!isRecord(payload)) throw new OrganizationAccessApiError("Invalid structure deletion result", 500);
    return { deletedNodes: requiredArray(payload, "deletedNodes").map(parseVisibleScope) };
};

const mutateInvitationLifecycle = async (
    organizationId: string,
    invitationId: string,
    action: "resend" | "revoke",
    input: InvitationLifecycleInput | RevokeInvitationInput,
    idToken?: string
): Promise<OrganizationInvitation> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}/${action}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
            body: JSON.stringify(input)
        }
    );
    return parseInvitation(payload);
};

export const resendOrganizationInvitation = async (
    organizationId: string,
    invitationId: string,
    input: InvitationLifecycleInput,
    idToken?: string
): Promise<OrganizationInvitation> => mutateInvitationLifecycle(organizationId, invitationId, "resend", input, idToken);

export const revokeOrganizationInvitation = async (
    organizationId: string,
    invitationId: string,
    input: RevokeInvitationInput,
    idToken?: string
): Promise<OrganizationInvitation> => mutateInvitationLifecycle(organizationId, invitationId, "revoke", input, idToken);

/** Exchange a raw email token for a privacy-safe, cross-tab HttpOnly pending session. */
export const createInvitationPendingSession = async (token: string, intent: string): Promise<void> => {
    await requestNoContent("/api/organization-access/invitations/pending-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, intent })
    });
};

/** Open a recipient review from the authenticated browser's pending session. */
export const openInvitationReview = async (): Promise<InvitationReview> => {
    const payload = await requestJson("/api/organization-access/invitations/review-session", {
        method: "POST"
    });
    return parseInvitationReview(payload);
};

/** Refresh the current exact review set using only the HttpOnly review session. */
export const refreshInvitationReview = async (): Promise<InvitationReview> => {
    const payload = await requestJson("/api/organization-access/invitations/review-session/refresh", {
        method: "POST"
    });
    return parseInvitationReview(payload);
};

/** Apply one explicit Accept-all or Decline-all decision to the exact reviewed set. */
export const decideInvitationReview = async (input: InvitationDecisionInput): Promise<InvitationDecisionResult> => {
    const payload = await requestJson("/api/organization-access/invitations/review-decision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input)
    });
    return parseInvitationDecisionResult(payload);
};

/**
 * Atomically edit Organization-managed profile fields for one Membership.
 * @throws {OrganizationAccessApiError} On validation, authorization, stale data, or transport failure.
 */
export const editOrganizationMemberProfile = async (
    organizationId: string,
    membershipId: string,
    input: EditMemberProfileInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/profile`,
        {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
            },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const addOrganizationMemberAssignmentCard = async (
    organizationId: string,
    membershipId: string,
    input: AssignmentCardMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/assignment-cards`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const addOrganizationMemberAccessAssignment = async (
    organizationId: string,
    membershipId: string,
    input: AccessAssignmentMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/access-assignments`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const editOrganizationMemberAccessAssignment = async (
    organizationId: string,
    membershipId: string,
    roleAssignmentId: string,
    input: AccessAssignmentMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/access-assignments/${encodeURIComponent(roleAssignmentId)}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const removeOrganizationMemberAccessAssignment = async (
    organizationId: string,
    membershipId: string,
    roleAssignmentId: string,
    input: RemoveAccessAssignmentInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/access-assignments/${encodeURIComponent(roleAssignmentId)}`,
        {
            method: "DELETE",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const editOrganizationMemberAssignmentCard = async (
    organizationId: string,
    membershipId: string,
    workAssignmentId: string,
    roleAssignmentId: string,
    input: AssignmentCardMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/assignment-cards/${encodeURIComponent(workAssignmentId)}/${encodeURIComponent(roleAssignmentId)}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const removeOrganizationMemberAssignmentCard = async (
    organizationId: string,
    membershipId: string,
    workAssignmentId: string,
    roleAssignmentId: string,
    input: RemoveAssignmentCardInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/assignment-cards/${encodeURIComponent(workAssignmentId)}/${encodeURIComponent(roleAssignmentId)}`,
        {
            method: "DELETE",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

const unitResourcePath = (organizationId: string, unitId: string, suffix: string): string =>
    `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/units/${encodeURIComponent(unitId)}/${suffix}`;

const authorizationHeaders = (idToken?: string): Record<string, string> => (idToken ? { Authorization: `Bearer ${idToken}` } : {});

export const unassignOrganizationMemberFromScope = async (
    organizationId: string,
    membershipId: string,
    workAssignmentId: string,
    input: MemberAccessMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/work-assignments/${encodeURIComponent(workAssignmentId)}/unassign`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const suspendOrganizationMember = async (
    organizationId: string,
    membershipId: string,
    input: MemberStatusMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/suspend`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const restoreOrganizationMember = async (
    organizationId: string,
    membershipId: string,
    input: MemberStatusMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/restore`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const removeOrganizationMember = async (
    organizationId: string,
    membershipId: string,
    input: MemberStatusMutationInput,
    idToken?: string
): Promise<UpdatedMemberProfile> => {
    const payload = await requestJson(
        `/api/organization-access/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(membershipId)}/remove`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
            body: JSON.stringify(input)
        }
    );
    return parseUpdatedMemberProfile(payload);
};

export const getUnitClients = async (organizationId: string, unitId: string, idToken?: string, signal?: AbortSignal): Promise<UnitClient[]> => {
    const payload = await requestJson(unitResourcePath(organizationId, unitId, "clients"), {
        headers: authorizationHeaders(idToken),
        ...(signal ? { signal } : {})
    });
    if (!isRecord(payload)) throw new OrganizationAccessApiError("Invalid Unit Client directory", 500);
    return requiredArray(payload, "clients").map(parseUnitClient);
};

export const createUnitClient = async (organizationId: string, unitId: string, input: UnitClientInput, idToken?: string): Promise<UnitClient> => {
    const payload = await requestJson(unitResourcePath(organizationId, unitId, "clients"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
        body: JSON.stringify(input)
    });
    return parseUnitClient(payload);
};

export const editUnitClient = async (
    organizationId: string,
    unitId: string,
    clientId: string,
    input: UnitClientInput & { expectedVersion: number },
    idToken?: string
): Promise<UnitClient> => {
    const payload = await requestJson(`${unitResourcePath(organizationId, unitId, "clients")}/${encodeURIComponent(clientId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
        body: JSON.stringify(input)
    });
    return parseUnitClient(payload);
};

export const deleteUnitClient = async (
    organizationId: string,
    unitId: string,
    clientId: string,
    expectedVersion: number,
    idToken?: string
): Promise<UnitClient> => {
    const payload = await requestJson(`${unitResourcePath(organizationId, unitId, "clients")}/${encodeURIComponent(clientId)}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
        body: JSON.stringify({ expectedVersion })
    });
    return parseUnitClient(payload);
};

export const getUnitStaff = async (organizationId: string, unitId: string, idToken?: string, signal?: AbortSignal): Promise<UnitStaffMember[]> => {
    const payload = await requestJson(unitResourcePath(organizationId, unitId, "staff"), {
        headers: authorizationHeaders(idToken),
        ...(signal ? { signal } : {})
    });
    if (!isRecord(payload)) throw new OrganizationAccessApiError("Invalid Unit Staff directory", 500);
    return requiredArray(payload, "staff").map(parseUnitStaffMember);
};

export const getUnitStaffCandidates = async (organizationId: string, unitId: string, idToken?: string): Promise<UnitStaffCandidate[]> => {
    const payload = await requestJson(unitResourcePath(organizationId, unitId, "staff/candidates"), { headers: authorizationHeaders(idToken) });
    if (!isRecord(payload)) throw new OrganizationAccessApiError("Invalid Unit Staff candidates", 500);
    return requiredArray(payload, "candidates").map(parseUnitStaffCandidate);
};

export const assignUnitStaff = async (organizationId: string, unitId: string, membershipId: string, idToken?: string): Promise<UnitStaffMember> => {
    const payload = await requestJson(unitResourcePath(organizationId, unitId, "staff"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authorizationHeaders(idToken) },
        body: JSON.stringify({ membershipId })
    });
    return parseUnitStaffMember(payload);
};

export const removeUnitWorkspaceStaff = async (organizationId: string, unitId: string, unitWorkspaceMembershipId: string, idToken?: string): Promise<void> => {
    await requestJson(`${unitResourcePath(organizationId, unitId, "staff")}/${encodeURIComponent(unitWorkspaceMembershipId)}/remove`, {
        method: "POST",
        headers: authorizationHeaders(idToken)
    });
};
